import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../../utils/i18n';
import cx from 'classnames';
import lodashIsEqual from 'lodash/isEqual';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import {
  default as TextInput,
  InputUITypes,
  ValidationTypes,
  Behaviors as TextInputBehaviors
} from '@adsk/bim360-matrix-react-text-input';
import Button from '@adsk/bim360-matrix-react-button';
import Checkbox from '@adsk/bim360-matrix-react-checkbox';
import StickyScrollableContainer from '@adsk/bim360-matrix-react-sticky-scrollable-container';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import AnimateHeightPopUp from '../../common/AnimateHeightPopUp';

import {
  isIssueAttributeDescriptionValid,
  isIssueAttributeListItemsValid,
  isIssueAttributeTitleValid,
  issueAttributeTitleValidators,
  prepareIssueAttributeOptionsForBackend
} from '../../../utils/issueAttributes-helpers';
import IssueAttributesAdminDraggableList from './IssueAttributesAdminDraggableList';
import IssueAttributesAdminTypeIconAndName from './IssueAttributesAdminTypeIconAndName';
import {
  ISSUE_ATTRIBUTE_MAX_DESCRIPTION_LENGTH,
  ISSUE_ATTRIBUTES_DATA_TYPES,
  ISSUE_ATTRIBUTES_TYPE_LIMITATIONS,
  ISSUE_ATTRIBUTES_TYPES_DROPDOWN_ORDER
} from '../../../constants/issue-attributes';
import SelectBoxAdapter from '../../common/SelectBoxAdapter';

class IssueAttributesAdminEditModal extends PureComponent {
  constructor(props) {
    super(props);

    this.issueAttributeTypes = ISSUE_ATTRIBUTES_TYPES_DROPDOWN_ORDER.map(key => ({
      label: key,
      value: key
    }));

    this.state = {
      ...this.getModalDefaults(props.currentIssueAttribute)
    };

    this.titleInputProps = {
      behavior: TextInputBehaviors.SINGLE_LINE,
      autofocus: true,
      onChange: value => {
        const valid = isIssueAttributeTitleValid(value);
        this.setState({title: {isNew: false, value, valid}, isDirty: true});
      },
      placeholder: i18n.t('issueAttributesAdmin.modal.titlePlaceholder'),
      className: 'IssueAttributesAdminEditModal__textfield',
      'data-testid': 'IssueAttributesAdminEditModal__title'
    };

    this.descriptionInputProps = {
      behavior: TextInputBehaviors.MULTI_LINE,
      onChange: value => {
        const valid = isIssueAttributeDescriptionValid(value);
        this.setState({description: {value, valid}, isDirty: true});
      },
      className: 'IssueAttributesAdminEditModal__description',
      placeholder: i18n.t('issueAttributesAdmin.modal.descriptionPlaceholder'),
      'data-testid': 'IssueAttributesAdminEditModal__description'
    };

    this.descriptionValidationProps = {
      type: ValidationTypes.SUBTITLE,
      required: false,
      validator: {
        message: i18n.t('issueAttributesAdmin.modal.maxCharactersLimit', {
          max: ISSUE_ATTRIBUTE_MAX_DESCRIPTION_LENGTH
        }),
        validate: text => isIssueAttributeDescriptionValid(text)
      }
    };

    this.onCreateSuccess = this.onCreateSuccess.bind(this);
    this.onUpdateSuccess = this.onUpdateSuccess.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.createIssueAttribute = this.createIssueAttribute.bind(this);
    this.updateIssueAttribute = this.updateIssueAttribute.bind(this);
    this.toggleCreateAnother = this.toggleCreateAnother.bind(this);
    this.onDraggableListChange = this.onDraggableListChange.bind(this);
    this.onAttributeTypeChange = this.onAttributeTypeChange.bind(this);
    this.onAnswerRemove = this.onAnswerRemove.bind(this);
    this.closeAnswerRemoveConfirmationPopup = this.closeAnswerRemoveConfirmationPopup.bind(this);
    this.removeAnswerAfterConfirmation = this.removeAnswerAfterConfirmation.bind(this);
    this.navigateToIssueTypes = this.navigateToIssueTypes.bind(this);
    this.setTitleInputRef = this.setTitleInputRef.bind(this);
    this._renderDropdownOptions = this._renderDropdownOptions.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.currentIssueAttribute !== nextProps.currentIssueAttribute) {
      this.setState({...this.getModalDefaults(nextProps.currentIssueAttribute)});
    }
  }

  getChangeObject() {
    const changeObject = {};
    const {currentIssueAttribute} = this.props;
    const {description, title, options} = this.state;

    if (currentIssueAttribute.title !== title.value) {
      changeObject.title = this.state.title.value;
    }
    if (currentIssueAttribute.description !== description.value) {
      changeObject.description = description.value;
    }

    if (ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST === currentIssueAttribute.dataType) {
      const previousOptions = currentIssueAttribute.metadata[currentIssueAttribute.dataType].options.map(
        option => option.value
      );
      const currentOptions = options.map(answer => answer.value);

      if (!lodashIsEqual(previousOptions, currentOptions)) {
        changeObject.metadata = prepareIssueAttributeOptionsForBackend(
          this.stripOptionValidationProps(this.state.options),
          currentIssueAttribute.dataType
        );
      }
    }
    return changeObject;
  }

  getTitleValidationProps() {
    return {
      type: ValidationTypes.SUBTITLE,
      required: true,
      ignore: this.state.title.isNew,
      validator: issueAttributeTitleValidators()
    };
  }

  getModalDefaults(currentIssueAttribute) {
    const isEditing = !isNullOrUndefined(currentIssueAttribute);
    return {
      isEditing,
      title: {
        isNew: !isEditing,
        value: isEditing ? currentIssueAttribute.title : '',
        valid: isEditing
      },
      description: {
        value: isEditing ? currentIssueAttribute.description : '',
        valid: true
      },
      dataType: isEditing
        ? this.issueAttributeTypes.find(dataType => dataType.value === currentIssueAttribute.dataType)
        : this.issueAttributeTypes[0],
      options: this.prepareOptions(
        isEditing && currentIssueAttribute.dataType === ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST
          ? currentIssueAttribute.metadata[currentIssueAttribute.dataType].options
          : []
      ),
      createAnother: false,
      isDirty: false,
      showAnswerRemoveConfirmation: false,
      answerIndexForDeletion: null
    };
  }

  onCreateSuccess() {
    if (this.props.isOpen) {
      if (this.state.createAnother) {
        this.setState({...this.getModalDefaults(), createAnother: true});
        this.titleRef && this.titleRef.focus();
      } else {
        this.closeModal();
        this.props.onCreate();
      }
    }
    // Unhighlight the newly added attribute in the table after X seconds and scroll down to it.
    setTimeout(() => {
      this.props.resetHighlightingRowsAction();
    }, 3000);
  }

  onUpdateSuccess() {
    if (this.props.isOpen) {
      this.closeModal();
    }
  }

  createIssueAttribute() {
    if (!this.isFormValid()) {
      return;
    }

    const createObject = {
      title: this.state.title.value.trim(),
      dataType: this.state.dataType.value,
      description: this.state.description.value ? this.state.description.value.trim() : undefined
    };

    if (this.state.dataType.value === ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST) {
      createObject.metadata = prepareIssueAttributeOptionsForBackend(
        this.stripOptionValidationProps(this.state.options),
        this.state.dataType.value
      );
    }

    const meta = {
      onSuccess: this.onCreateSuccess,
      createAnother: this.state.createAnother
    };

    this.props.createIssueAttributeAction(createObject, meta);
  }

  updateIssueAttribute() {
    if (!this.isFormValid()) {
      return;
    }
    this.props.updateIssueAttributeAction(this.getChangeObject(), {
      onSuccess: this.onUpdateSuccess,
      id: this.props.currentIssueAttribute.id,
      title: this.state.title.value
    });
  }

  toggleCreateAnother(createAnother) {
    this.setState({createAnother});
  }

  closeModal() {
    this.props.closeModal();
    this.setState({...this.getModalDefaults()});
  }

  isAllowedToCreateType() {
    return (
      this.props.definitionTypesCount &&
      (!this.props.definitionTypesCount[this.state.dataType] ||
        this.props.definitionTypesCount[this.state.dataType] < ISSUE_ATTRIBUTES_TYPE_LIMITATIONS[this.state.dataType])
    );
  }

  areOptionsValid() {
    return this.state.dataType.value === ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST
      ? isIssueAttributeListItemsValid(this.state.options)
      : true;
  }

  isFormValid() {
    return (
      !this.props.isSaving &&
      this.state.title.valid &&
      this.state.description.valid &&
      !!this.state.dataType &&
      this.isAllowedToCreateType() &&
      this.areOptionsValid()
    );
  }

  prepareOptions(options) {
    if (options.length === 0) {
      return [{value: '', isNew: true, valid: false}];
    }
    return options.map(option => ({...option, isNew: false, valid: true}));
  }

  stripOptionValidationProps(options) {
    return options
      .map(option => ({...option}))
      .map(option => {
        delete option.isNew;
        delete option.valid;
        return option;
      });
  }

  onDraggableListChange(newOptions) {
    this.setState({options: newOptions, isDirty: true});
  }

  onAttributeTypeChange(value) {
    this.setState({dataType: value, options: this.prepareOptions([])});
  }

  removeAnswer(i) {
    const newOptions = [...this.state.options];
    newOptions.splice(i, 1);
    this.onDraggableListChange(newOptions);
  }

  onAnswerRemove(i) {
    if (this.state.isEditing) {
      this.setState({showAnswerRemoveConfirmation: true, answerIndexForDeletion: i});
    } else {
      this.removeAnswer(i);
    }
  }

  closeAnswerRemoveConfirmationPopup() {
    this.setState({showAnswerRemoveConfirmation: false, answerIndexForDeletion: null});
  }

  removeAnswerAfterConfirmation() {
    this.removeAnswer(this.state.answerIndexForDeletion);
    this.closeAnswerRemoveConfirmationPopup();
  }

  navigateToIssueTypes() {
    this.props.changeLocationAction('/admin/issueTypes');
  }

  setTitleInputRef(ref) {
    this.titleRef = ref;
  }

  _renderConfirmationPopup() {
    const {showAnswerRemoveConfirmation} = this.state;
    return (
      <AnimateHeightPopUp
        isOpen={showAnswerRemoveConfirmation}
        className="IssueAttributesAdminEditModal__animated-confirmation"
      >
        <div
          className="IssueAttributesAdminEditModal__confirmation"
          data-testid="IssueAttributesAdminEditModal__confirmation"
        >
          <div
            className="IssueAttributesAdminEditModal__confirmation-text"
            data-testid="IssueAttributesAdminEditModal__confirmation-text"
          >
            {i18n.t('issueAttributesAdmin.modal.optionDeleteWarning')}
          </div>
          <div className="IssueAttributesAdminEditModal__confirmation-footer">
            <Button
              styleType={Button.StyleType.LINK_SECONDARY}
              onClick={this.closeAnswerRemoveConfirmationPopup}
              className="IssueAttributesAdminEditModal__button IssueAttributesAdminEditModal__button--cancel"
              tabIndex="0"
              data-testid="IssueAttributesAdminEditModal__confirmation--cancel"
            >
              {i18n.t('issueAttributesAdmin.modal.cancel')}
            </Button>
            <Button
              styleType={Button.StyleType.PRIMARY}
              onClick={this.removeAnswerAfterConfirmation}
              className="IssueAttributesAdminEditModal__button"
              tabIndex="0"
              data-testid="IssueAttributesAdminEditModal__confirmation--confirm"
            >
              {i18n.t('issueAttributesAdmin.modal.remove')}
            </Button>
          </div>
        </div>
      </AnimateHeightPopUp>
    );
  }

  _renderSelectTypeOptions() {
    return (
      <div>
        <div
          className="IssueAttributesAdminEditModal__text"
          data-testid="IssueAttributesAdminEditModal__options-field-header"
        >
          <span>{i18n.t('issueAttributesAdmin.modal.optionsField')}</span>
          <span className="IssueAttributesAdminEditModal__required"> *</span>
        </div>
        <IssueAttributesAdminDraggableList
          options={this.state.options}
          onChange={this.onDraggableListChange}
          container={this.props.container}
          onAnswerRemove={this.onAnswerRemove}
        />
      </div>
    );
  }

  _renderDropdownOptions(dataType) {
    return (
      <IssueAttributesAdminTypeIconAndName
        dataType={dataType}
        container={this.props.container}
        svgIconDataTestId="IssueAttributesAdminEditModal__type-icon"
        titleDataTestId="IssueAttributesAdminEditModal__type-name"
        disabled={this.state.isEditing}
      />
    );
  }

  _renderModalBody() {
    const dropdownCls = cx('IssueAttributesAdminEditModal__dropdown', {
      'IssueAttributesAdminEditModal__dropdown--type-error': !this.isAllowedToCreateType()
    });

    const issueTypesText = this.state.isEditing ? (
      <Button
        styleType={Button.StyleType.INLINE_LINK}
        className="IssueAttributesAdminEditModal__instructions-button"
        data-testid="IssueAttributesAdminEditModal__instructions-button"
        onClick={this.navigateToIssueTypes}
      >
        {i18n.t('issueAttributesAdmin.modal.issueTypes')}
      </Button>
    ) : (
      i18n.t('issueAttributesAdmin.modal.issueTypes')
    );

    return (
      <div className="IssueAttributesAdminEditModal__body">
        <div className="IssueAttributesAdminEditModal__text">
          <span>{i18n.t('issueAttributesAdmin.modal.titleField')}</span>
          <span className="IssueAttributesAdminEditModal__required"> *</span>
        </div>
        <TextInput
          value={this.state.title.value}
          inputUIType={InputUITypes.NORMAL}
          inputProps={this.titleInputProps}
          validationProps={this.getTitleValidationProps()}
          ref={this.setTitleInputRef}
        />
        <div className="IssueAttributesAdminEditModal__text">
          <span>{i18n.t('issueAttributesAdmin.modal.typeField')}</span>
        </div>
        <SelectBoxAdapter
          options={this.issueAttributeTypes}
          className={dropdownCls}
          data-testid="IssueAttributesAdminEditModal__type"
          onChange={this.onAttributeTypeChange}
          selectedOptions={this.state.dataType}
          optionRenderer={this._renderDropdownOptions}
          valueRenderer={this._renderDropdownOptions}
          disabled={this.state.isEditing}
          openOnFocus={false}
        />
        {!this.isAllowedToCreateType() && (
          <div className="IssueAttributesAdminEditModal__dropdown--error">
            {i18n.t('issueAttributesAdmin.modal.typeLimitReached')}
          </div>
        )}
        {this.state.dataType.value === ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST && this._renderSelectTypeOptions()}
        <div className="IssueAttributesAdminEditModal__text">
          <span>{i18n.t('issueAttributesAdmin.modal.descriptionField')}</span>
        </div>
        <TextInput
          value={this.state.description.value}
          inputUIType={InputUITypes.NORMAL}
          inputProps={this.descriptionInputProps}
          validationProps={this.descriptionValidationProps}
        />
        <div className="IssueAttributesAdminEditModal__instructions">
          <SvgIcon
            svgId="info-circle-small"
            className="IssueAttributesAdminEditModal__instructions-icon"
            width="16px"
            height="16px"
          />
          <div>
            {i18n.t('issueAttributesAdmin.modal.createExplanation')}
            {issueTypesText}
            {i18n.t('issueAttributesAdmin.modal.tab')}
          </div>
        </div>
      </div>
    );
  }

  _renderModalFooter() {
    const {isSaving} = this.props;
    const {createAnother, isDirty, isEditing} = this.state;
    const createButtonText = isSaving
      ? i18n.t('issueAttributesAdmin.modal.inProgress')
      : this.state.isEditing
        ? i18n.t('issueAttributesAdmin.modal.save')
        : i18n.t('issueAttributesAdmin.modal.create');

    const isDisabled = !isDirty || !this.isFormValid();

    return (
      <div className="IssueAttributesAdminEditModal__footer">
        <div className="IssueAttributesAdminEditModal__footer-right">
          <Button
            styleType={Button.StyleType.LINK_SECONDARY}
            onClick={this.closeModal}
            className="IssueAttributesAdminEditModal__button IssueAttributesAdminEditModal__button--cancel"
            tabIndex="0"
            data-testid="IssueAttributesAdminEditModal__button--cancel"
          >
            {i18n.t('issueAttributesAdmin.modal.cancel')}
          </Button>
          <Button
            styleType={Button.StyleType.PRIMARY}
            onClick={isEditing ? this.updateIssueAttribute : this.createIssueAttribute}
            className="IssueAttributesAdminEditModal__button"
            disabled={isDisabled}
            tabIndex="0"
            data-testid="IssueAttributesAdminEditModal__button--create"
          >
            {createButtonText}
          </Button>
        </div>
        <div className="IssueAttributesAdminEditModal__footer-left">
          {!isEditing && (
            <Checkbox
              onChange={this.toggleCreateAnother}
              checked={createAnother}
              data-testid="IssueAttributesAdminEditModal__create-another"
              label={i18n.t('issueAttributesAdmin.modal.createAnother')}
            />
          )}
        </div>
      </div>
    );
  }

  render() {
    const {isOpen, isSaving, container} = this.props;
    const title = this.state.isEditing
      ? i18n.t('issueAttributesAdmin.modal.editTitle')
      : i18n.t('issueAttributesAdmin.modal.title');

    const stickyClass = cx('IssueAttributesAdminEditModal__sticky', {
      'IssueAttributesAdminEditModal__sticky--fixed': this.state.answerIndexForDeletion !== null
    });

    return (
      <SimpleModal
        container={container}
        title={title}
        show={isOpen}
        footer={this._renderModalFooter()}
        onExited={this.closeModal}
        disableModalExit={isSaving}
        className="IssueAttributesAdminEditModal"
      >
        <StickyScrollableContainer className={stickyClass}>
          <StickyScrollableContainer.Header>{this._renderConfirmationPopup()}</StickyScrollableContainer.Header>
          <StickyScrollableContainer.Content className="IssueAttributesAdminEditModal__scrollable-container">
            {this._renderModalBody()}
          </StickyScrollableContainer.Content>
        </StickyScrollableContainer>
      </SimpleModal>
    );
  }
}

IssueAttributesAdminEditModal.displayName = 'IssueAttributesAdminEditModal';

IssueAttributesAdminEditModal.propTypes = {
  definitionTypesCount: PropTypes.object,
  container: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  createIssueAttributeAction: PropTypes.func.isRequired,
  updateIssueAttributeAction: PropTypes.func.isRequired,
  resetHighlightingRowsAction: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  changeLocationAction: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  currentIssueAttribute: PropTypes.object
};

export default IssueAttributesAdminEditModal;
